import {
  ProgressTimestamp,
  TransactionProgressTracker,
} from "./TransactionProgressTracker";

type BookingStatus =
  | "pending"
  | "processing"
  | "approved"
  | "declined"
  | "matured";

type PaymentStatus = "pending" | "validated" | "invalid";

export type TrackerStepState = "complete" | "current" | "upcoming" | "failed";

export interface InvestmentTransaction {
  [key: string]: unknown;
  status?: unknown;
  transactionType?: unknown;
  amount?: unknown;
  shares?: unknown;
  rateAtBooking?: unknown;
  fundDetails?: unknown;
  fundCompanyDetails?: unknown;
  datePlaced?: unknown;
  dateProcessed?: unknown;
  bookingUploadedAt?: unknown;
  paymentValid?: unknown;
  paymentValidation?: unknown;
  redemptionType?: unknown;
  settlementDetails?: unknown;
  redemptionProgress?: unknown;
}

export interface InvestmentTrackerStep {
  id: "submitted" | "payment" | "booking" | "allocation";
  number: number;
  label: string;
  title: string;
  description: string;
  state: TrackerStepState;
  chip: "Completed" | "In progress" | "Pending" | "Declined";
  timestamp?: ProgressTimestamp;
  icon?: "payment" | "processing" | "request";
  date?: string;
}

const BOOKING_STATUSES = new Set<BookingStatus>([
  "pending",
  "processing",
  "approved",
  "declined",
  "matured",
]);

const REDEMPTION_STATUSES = new Set([
  "pendingwithdrawal",
  "pendingRedemption",
  "processingRedemption",
  "redeemed",
  "declinedredemption",
]);

const REINVESTMENT_TYPES = new Set([
  "reinvestment",
  "dividendReinvestment",
  "dividend-reinvestment",
]);

const PAYMENT_STATUSES = new Set<PaymentStatus>([
  "pending",
  "validated",
  "invalid",
]);

function isKnownPaymentStatus(value: unknown): value is PaymentStatus {
  return (
    typeof value === "string" && PAYMENT_STATUSES.has(value as PaymentStatus)
  );
}

function hasRedemptionEvidence(transaction: InvestmentTransaction): boolean {
  const progress = transaction.redemptionProgress;
  const hasTrackedProgress =
    progress !== null &&
    typeof progress === "object" &&
    (progress as { version?: unknown }).version === 1;

  return (
    hasTrackedProgress ||
    (transaction.redemptionType !== undefined &&
      transaction.redemptionType !== null &&
      transaction.redemptionType !== "") ||
    (transaction.settlementDetails !== undefined &&
      transaction.settlementDetails !== null &&
      transaction.settlementDetails !== "")
  );
}

export function isInvestmentTransaction(
  transaction: InvestmentTransaction
): boolean {
  const status = transaction.status;

  if (typeof status !== "string" || REDEMPTION_STATUSES.has(status)) {
    return false;
  }

  if (!BOOKING_STATUSES.has(status as BookingStatus)) {
    return false;
  }

  return status !== "declined" || !hasRedemptionEvidence(transaction);
}

export type TransactionKind = "investment" | "redemption" | "unknown";

export function getTransactionKind(
  transaction: InvestmentTransaction
): TransactionKind {
  if (isInvestmentTransaction(transaction)) {
    return "investment";
  }

  const status = transaction.status;
  if (
    typeof status === "string" &&
    (REDEMPTION_STATUSES.has(status) ||
      (status === "declined" && hasRedemptionEvidence(transaction)))
  ) {
    return "redemption";
  }

  return "unknown";
}

function isReinvestment(transaction: InvestmentTransaction): boolean {
  return (
    typeof transaction.transactionType === "string" &&
    REINVESTMENT_TYPES.has(transaction.transactionType)
  );
}

function getPaymentStatus(transaction: InvestmentTransaction): {
  status: PaymentStatus;
  updatedAt?: unknown;
} {
  const paymentValidation = transaction.paymentValidation;

  if (paymentValidation && typeof paymentValidation === "object") {
    const canonicalStatus = (paymentValidation as { status?: unknown }).status;
    if (isKnownPaymentStatus(canonicalStatus)) {
      return {
        status: canonicalStatus,
        updatedAt: (paymentValidation as { updatedAt?: unknown }).updatedAt,
      };
    }
  }

  if (isKnownPaymentStatus(paymentValidation)) {
    return { status: paymentValidation };
  }

  if (isKnownPaymentStatus(transaction.paymentValid)) {
    return { status: transaction.paymentValid };
  }

  return { status: "pending" };
}

function validDate(date: Date): Date | null {
  return Number.isNaN(date.getTime()) ? null : date;
}

function isValidCalendarDate(
  year: number,
  month: number,
  day: number
): boolean {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function parseCalendarDate(
  year: number,
  month: number,
  day: number
): Date | null {
  return isValidCalendarDate(year, month, day)
    ? new Date(year, month - 1, day)
    : null;
}

function parseIsoDateTime(value: string): Date | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d)?$/.exec(
      value
    );

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || 0);

  if (
    !isValidCalendarDate(year, month, day) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  const timezone = match[8];
  if (!timezone) {
    const milliseconds = Number((match[7] || "").padEnd(3, "0") || 0);
    return validDate(
      new Date(year, month - 1, day, hour, minute, second, milliseconds)
    );
  }

  return validDate(new Date(value));
}

function parseDateString(value: string): Date | null {
  const trimmed = value.trim();
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  const slashDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);

  if (dateOnlyMatch) {
    return parseCalendarDate(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]),
      Number(dateOnlyMatch[3])
    );
  }

  if (slashDateMatch) {
    return parseCalendarDate(
      Number(slashDateMatch[3]),
      Number(slashDateMatch[1]),
      Number(slashDateMatch[2])
    );
  }

  return parseIsoDateTime(trimmed);
}

export function toTransactionDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return validDate(value);
  }

  if (value && typeof value === "object") {
    const timestamp = value as {
      toDate?: unknown;
      seconds?: unknown;
      nanoseconds?: unknown;
      _seconds?: unknown;
      _nanoseconds?: unknown;
    };

    try {
      if (typeof timestamp.toDate === "function") {
        const date = timestamp.toDate();
        return date instanceof Date ? validDate(date) : null;
      }
    } catch (_error) {
      return null;
    }

    const seconds = timestamp.seconds ?? timestamp._seconds;
    if (seconds !== undefined && seconds !== null) {
      const secondsNumber = Number(seconds);
      const nanosecondsNumber = Number(
        timestamp.nanoseconds ?? timestamp._nanoseconds ?? 0
      );

      if (
        Number.isFinite(secondsNumber) &&
        Number.isFinite(nanosecondsNumber)
      ) {
        return validDate(
          new Date(secondsNumber * 1000 + nanosecondsNumber / 1000000)
        );
      }
    }

    return null;
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  if (typeof value === "string") {
    return value.trim() === "" ? null : parseDateString(value);
  }

  return validDate(new Date(value));
}

export function formatMilestoneDate(value: unknown): string {
  const date = toTransactionDate(value);

  if (!date) {
    return "Date unavailable";
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
}

export function formatMilestoneTimestamp(value: unknown): ProgressTimestamp {
  const date = toTransactionDate(value);

  if (!date) {
    return { display: "Date unavailable", precision: "date" };
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const display = `${month}/${day}/${date.getFullYear()}`;
  const dateTime = `${date.getFullYear()}-${month}-${day}`;

  return {
    display,
    dateTime,
    precision: "date",
  };
}

export function getInvestmentHistoryStatus(
  transaction: InvestmentTransaction
): string {
  const status = transaction.status;
  const normalizedStatus =
    typeof status === "string" && status
      ? (status.charAt(0).toUpperCase() + status.slice(1)).replace(
          /([a-z](?=[A-Z]))/g,
          "$1 "
        )
      : "";

  if (getTransactionKind(transaction) !== "redemption") {
    return normalizedStatus;
  }

  const progress = transaction.redemptionProgress;
  if (
    progress &&
    typeof progress === "object" &&
    (progress as { version?: unknown }).version === 1
  ) {
    const stage = (progress as { stage?: unknown }).stage;

    if (stage === "credited") return "Redeemed";
    if (stage === "declined") return "Declined";
    if (
      stage === "requested" ||
      stage === "confirmed" ||
      stage === "waitingForProceeds"
    ) {
      return "Pending Redemption";
    }
  }

  if (
    status === "pendingwithdrawal" ||
    status === "pendingRedemption" ||
    status === "processingRedemption"
  ) {
    return "Pending Redemption";
  }

  return normalizedStatus;
}

function chipForState(
  state: TrackerStepState
): "Completed" | "In progress" | "Pending" | "Declined" {
  if (state === "complete") return "Completed";
  if (state === "current") return "In progress";
  if (state === "failed") return "Declined";
  return "Pending";
}

export function deriveInvestmentSteps(
  transaction: InvestmentTransaction,
  processingTimeline?: string
): InvestmentTrackerStep[] | null {
  if (!isInvestmentTransaction(transaction)) {
    return null;
  }

  const status = transaction.status as BookingStatus;
  const reinvestment = isReinvestment(transaction);
  const payment = getPaymentStatus(transaction);
  const invalidPayment = payment.status === "invalid";
  const finalized = status === "approved" || status === "matured";
  const declinedBooking = status === "declined";
  const paymentComplete =
    !invalidPayment &&
    (reinvestment || payment.status === "validated" || finalized);
  const bookingFailed = !invalidPayment && declinedBooking;
  const effectiveFinalized = finalized && !invalidPayment;

  const paymentState: TrackerStepState = invalidPayment
    ? "failed"
    : paymentComplete
    ? "complete"
    : bookingFailed
    ? "upcoming"
    : "current";

  const bookingState: TrackerStepState = invalidPayment
    ? "upcoming"
    : bookingFailed
    ? "failed"
    : effectiveFinalized
    ? "complete"
    : paymentComplete
    ? "current"
    : "upcoming";

  const paymentTimestamp = invalidPayment
    ? formatMilestoneTimestamp(payment.updatedAt)
    : reinvestment
    ? { display: "Not applicable", precision: "date" as const }
    : payment.status === "validated" ||
      payment.status === "invalid" ||
      finalized
    ? formatMilestoneTimestamp(payment.updatedAt)
    : undefined;

  const submittedTimestamp = formatMilestoneTimestamp(transaction.datePlaced);
  const bookingTimestamp = effectiveFinalized
    ? formatMilestoneTimestamp(transaction.dateProcessed)
    : bookingFailed
    ? formatMilestoneTimestamp(
        transaction.bookingUploadedAt ?? transaction.dateProcessed
      )
    : undefined;
  const allocationTimestamp = effectiveFinalized
    ? formatMilestoneTimestamp(
        transaction.bookingUploadedAt ?? transaction.dateProcessed
      )
    : undefined;

  const step = (
    value: Omit<InvestmentTrackerStep, "date">
  ): InvestmentTrackerStep => ({
    ...value,
    id: value.id,
    date: value.timestamp?.display.split(" · ")[0],
  });

  const steps = [
    step({
      id: "submitted",
      number: 1,
      label: "Investment Submitted",
      title: "Investment Submitted",
      description:
        "We’ve received your investment request and locked in your order.",
      state: "complete",
      chip: "Completed",
      timestamp: submittedTimestamp,
    }),
    step({
      id: "payment",
      label: invalidPayment
        ? "Payment Declined"
        : reinvestment
        ? "Payment Not Required"
        : paymentComplete
        ? "Payment Validated"
        : "Undergoing Payment Validation",
      number: 2,
      title: invalidPayment
        ? "Payment Declined"
        : reinvestment
        ? "Payment Not Required"
        : paymentComplete
        ? "Payment Validated"
        : "Undergoing Payment Validation",
      description: invalidPayment
        ? "We couldn’t validate your payment. Contact Support if you need assistance."
        : reinvestment
        ? "This investment was funded through dividend reinvestment."
        : paymentComplete
        ? "Your payment has been validated and your order is ready for booking."
        : "We’re confirming your payment has cleared. This usually takes about one business day.",
      state: paymentState,
      chip: chipForState(paymentState),
      timestamp: paymentTimestamp,
      icon: "payment",
    }),
    step({
      id: "booking",
      label: bookingFailed
        ? "Investment Declined"
        : effectiveFinalized
        ? "Investment Booked"
        : "Processing Investment",
      number: 3,
      title: bookingFailed
        ? "Investment Declined"
        : effectiveFinalized
        ? "Investment Booked"
        : "Processing Investment",
      description: bookingFailed
        ? "We couldn’t book this investment. Contact Support for next steps."
        : effectiveFinalized
        ? "Your investment was successful and has been reflected to your portfolio."
        : processingTimeline?.trim()
        ? `Your order has been booked and will be reflected within ${processingTimeline.trim()} days.`
        : "Your order has been booked and will be reflected at the fund’s NAVPU.",
      state: bookingState,
      chip: chipForState(bookingState),
      timestamp: bookingTimestamp,
      icon: "processing",
    }),
    step({
      id: "allocation",
      number: 4,
      label: "Investment Confirmed and Allocated",
      title: "Investment Confirmed and Allocated",
      description: effectiveFinalized
        ? "Your units have been allocated to your account."
        : "Units will be allocated once booking is complete.",
      state: effectiveFinalized ? "complete" : "upcoming",
      chip: chipForState(effectiveFinalized ? "complete" : "upcoming"),
      timestamp: allocationTimestamp,
      icon: "request",
    }),
  ];

  return effectiveFinalized
    ? steps.filter((step) => step.id !== "booking").map((step, index) => ({
        ...step,
        number: index + 1,
      }))
    : steps;
}

export interface InvestmentTrackerProps {
  transaction: InvestmentTransaction;
  processingTimeline?: string;
}

export function InvestmentTracker({
  transaction,
  processingTimeline,
}: InvestmentTrackerProps): JSX.Element | null {
  const steps = deriveInvestmentSteps(transaction, processingTimeline);

  if (!steps) {
    return null;
  }

  return (
    <TransactionProgressTracker
      steps={steps}
      ariaLabel="Investment progress steps"
    />
  );
}
